import React, { useState, useRef, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';

// ============================================
// THEMES - 5 Light, 5 Dark
// ============================================
const themes = {
  // Light themes
  paper: {
    name: 'Paper',
    bg: '#faf9f7',
    text: '#1c1917',
    textDim: '#a8a29e',
    orp: '#dc2626',
    surface: '#ffffff',
    border: '#e7e5e4',
    isLight: true,
  },
  sepia: {
    name: 'Sepia',
    bg: '#f5f0e6',
    text: '#3d3229',
    textDim: '#a69882',
    orp: '#b45309',
    surface: '#faf6ed',
    border: '#e5ddd0',
    isLight: true,
  },
  mint: {
    name: 'Mint',
    bg: '#f0fdf4',
    text: '#14532d',
    textDim: '#6b9a7d',
    orp: '#16a34a',
    surface: '#f7fef9',
    border: '#dcfce7',
    isLight: true,
  },
  sky: {
    name: 'Sky',
    bg: '#f0f9ff',
    text: '#0c4a6e',
    textDim: '#7499b5',
    orp: '#0284c7',
    surface: '#f8fbff',
    border: '#e0f2fe',
    isLight: true,
  },
  lavender: {
    name: 'Lavender',
    bg: '#faf5ff',
    text: '#3b0764',
    textDim: '#a78bba',
    orp: '#9333ea',
    surface: '#fdfaff',
    border: '#f3e8ff',
    isLight: true,
  },
  // Dark themes
  charcoal: {
    name: 'Charcoal',
    bg: '#1c1917',
    text: '#e7e5e4',
    textDim: '#78716c',
    orp: '#f59e0b',
    surface: '#292524',
    border: '#3d3835',
    isLight: false,
  },
  midnight: {
    name: 'Midnight',
    bg: '#0a0a0a',
    text: '#e8e8e8',
    textDim: '#666666',
    orp: '#ef4444',
    surface: '#151515',
    border: '#252525',
    isLight: false,
  },
  ocean: {
    name: 'Ocean',
    bg: '#0f172a',
    text: '#e2e8f0',
    textDim: '#64748b',
    orp: '#06b6d4',
    surface: '#1e293b',
    border: '#334155',
    isLight: false,
  },
  forest: {
    name: 'Forest',
    bg: '#0a0f0d',
    text: '#d1e7dd',
    textDim: '#5a7a6a',
    orp: '#22c55e',
    surface: '#141f1a',
    border: '#1e3a2f',
    isLight: false,
  },
  plum: {
    name: 'Plum',
    bg: '#1a0a1a',
    text: '#f0e6f0',
    textDim: '#8a6a8a',
    orp: '#d946ef',
    surface: '#261226',
    border: '#3d1f3d',
    isLight: false,
  },
};

// ============================================
// SPEED PRESETS
// ============================================
const speedPresets = [
  { name: 'Comfortable', wpm: 300, desc: 'Relaxed pace' },
  { name: 'Fast', wpm: 450, desc: 'Brisk reading' },
  { name: 'Flow', wpm: 600, desc: 'Optimal focus' },
  { name: 'Challenge', wpm: 800, desc: 'Push limits' },
];

const rampDurations = [
  { name: 'Quick', seconds: 5 },
  { name: 'Normal', seconds: 10 },
  { name: 'Gentle', seconds: 20 },
];

// ============================================
// SAMPLE TEXT
// ============================================
const sampleText = `The future of reading isn't about consuming more words per minute. It's about entering a state of focused attention where information flows directly into understanding. Traditional reading forces your eyes to jump across lines, scan backwards, and constantly reorient. This mechanical process creates friction between you and the text.

RSVP reading removes that friction entirely. By presenting words at a fixed point, your eyes can rest while your mind works. The colored anchor letter isn't just a visual aid — it's a focal point that trains your attention to stay centered and calm.

Most people discover something surprising when they first try this: comprehension doesn't require subvocalization. You don't need to hear each word in your head to understand it. Your brain is far more capable than that internal voice gives it credit for.

The goal isn't speed for its own sake. The goal is flow — that state where reading feels effortless, where pages seem to turn themselves, where you finish an article and realize you absorbed every idea without strain.

This is reading as it should feel. A conversation between the text and your mind, uninterrupted by the mechanics of moving your eyes or turning pages. Just pure understanding, flowing naturally from one thought to the next.`;

// ============================================
// TUTORIAL CONTENT
// ============================================
const createTutorialSegments = (targetWpm, enableRamp) => {
  const segments = [];
  
  segments.push({
    type: 'message',
    lines: ['Welcome.', 'Take a breath.'],
    duration: 3000,
  });
  
  segments.push({
    type: 'message',
    lines: ["You're about to try", "a different way of reading."],
    duration: 3000,
  });
  
  segments.push({
    type: 'message',
    lines: ['Words will appear one at a time', 'at the center of the screen.'],
    duration: 3500,
  });
  
  segments.push({
    type: 'message',
    lines: ['Each word has a colored letter —', "that's your anchor."],
    duration: 3500,
  });
  
  segments.push({
    type: 'message',
    lines: ['Keep your eyes gently focused', 'on that letter.'],
    duration: 3000,
  });
  
  segments.push({
    type: 'message', 
    lines: ['It stays perfectly still', 'while words change around it.'],
    duration: 3500,
  });

  const warmupWpm = 300;
  segments.push({
    type: 'message',
    lines: [`We'll start at ${warmupWpm}`, 'words per minute.'],
    duration: 2500,
  });
  
  segments.push({
    type: 'message',
    lines: ['That may already feel fast —', "and that's okay."],
    duration: 2500,
  });
  
  segments.push({
    type: 'reading',
    text: `Your mind may want to say each word out loud in your head. If that happens, don't fight it. Instead, let that inner voice soften gradually. Treat each word like a picture, not a sound. Just observe. Your brain already knows how to do this. You've been recognizing words instantly since childhood. The skill is already there. We're just removing the friction that slows it down. Let the words wash over you. Don't grab at them. Let them come to you naturally, one after another, like drops of rain on a window.`,
    wpm: warmupWpm,
  });
  
  segments.push({
    type: 'message',
    lines: ['Good.', "You're finding the rhythm."],
    duration: 2500,
  });

  if (enableRamp && targetWpm > 350) {
    const rampWpm = Math.min(450, Math.round(targetWpm * 0.75));
    
    segments.push({
      type: 'message',
      lines: [`Now moving up to ${rampWpm}`, 'words per minute.'],
      duration: 2500,
    });
    
    segments.push({
      type: 'message',
      lines: ['Notice something interesting:', "You're still understanding."],
      duration: 2500,
    });
    
    segments.push({
      type: 'reading',
      text: `Even though there's no time to subvocalize every word, your comprehension keeps working. That's because reading was never really about your inner voice. It was always about pattern recognition. Your brain sees a word shape and knows its meaning instantly. The voice in your head was just a passenger, not the driver. Trust your comprehension. It's working even now, even at this faster pace. Meaning emerges naturally when you stop trying to control it. You don't need to catch every single word perfectly. Your mind fills in the gaps automatically. It's been doing this your whole life. The words flow in and understanding flows out. That's all reading ever was.`,
      wpm: rampWpm,
    });
    
    segments.push({
      type: 'message',
      lines: ['Excellent.', "Feel how natural that was."],
      duration: 2500,
    });
  }

  segments.push({
    type: 'message',
    lines: [`Now arriving at ${targetWpm}`, 'words per minute.'],
    duration: 2500,
  });
  
  segments.push({
    type: 'message',
    lines: ['Focus on: the center letter,', 'the rhythm, the flow.'],
    duration: 3000,
  });
  
  segments.push({
    type: 'reading',
    text: `At this pace, something shifts. Reading stops feeling like work. It starts feeling like watching. The words arrive, and you understand them. There's no effort, no strain, no trying. Just a smooth stream of meaning flowing directly into your mind. This is what reading can feel like when you remove the mechanical obstacles. No jumping eyes. No backtracking. No losing your place. Just pure focus, pure flow, pure comprehension. You might notice that you've stopped thinking about the process entirely. That's the goal. When reading becomes invisible, when it's just you and the ideas, that's when real understanding happens. That's the state we're aiming for. Not speed for its own sake. But speed as a doorway to deeper focus.`,
    wpm: targetWpm,
  });
  
  segments.push({
    type: 'message',
    lines: ["You're ready.", "Let's begin your reading."],
    duration: 2500,
  });

  segments.push({ type: 'countdown', from: 3 });
  
  return segments;
};

// ============================================
// PDF TEXT EXTRACTION
// ============================================
async function extractTextFromPDF(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async function(e) {
      try {
        const typedArray = new Uint8Array(e.target.result);
        
        if (!window.pdfjsLib) {
          await new Promise((res, rej) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            script.onload = res;
            script.onerror = rej;
            document.head.appendChild(script);
          });
          
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }
        
        const pdf = await window.pdfjsLib.getDocument({ data: typedArray }).promise;
        let fullText = '';
        
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map(item => item.str)
            .join(' ');
          fullText += pageText + ' ';
        }
        
        fullText = fullText
          .replace(/\s+/g, ' ')
          .replace(/(\w)-\s+(\w)/g, '$1$2')
          .trim();
        
        resolve(fullText);
      } catch (err) {
        reject(err);
      }
    };
    
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// ============================================
// UTILITIES
// ============================================
function getORPIndex(word) {
  const len = word.length;
  if (len <= 1) return 0;
  if (len <= 3) return 0;
  if (len <= 5) return 1;
  if (len <= 9) return 2;
  return 3;
}

function tokenize(text) {
  const words = [];
  let sentenceIndex = 0;
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  
  tokens.forEach((token) => {
    const isSentenceEnd = /[.!?]$/.test(token);
    words.push({
      text: token,
      clean: token.replace(/[^a-zA-Z0-9'-]/g, ''),
      isSentenceEnd,
      sentenceIndex
    });
    if (isSentenceEnd) sentenceIndex++;
  });
  
  return words;
}

// Pause multiplier for sentence endings (2x = double the time)
const SENTENCE_PAUSE_MULTIPLIER = 2.0;

// ============================================
// COMPONENTS
// ============================================

// Split circle theme button showing bg and orp colors
function ThemeButton({ themeKey, themeData, isSelected, onClick }) {
  const size = 24;
  
  return (
    <button
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: isSelected ? `2px solid ${themeData.orp}` : `1px solid ${themeData.isLight ? themeData.border : '#444'}`,
        cursor: 'pointer',
        padding: 0,
        overflow: 'hidden',
        transform: isSelected ? 'scale(1.2)' : 'scale(1)',
        transition: 'transform 0.15s',
        position: 'relative',
        background: 'transparent'
      }}
      title={themeData.name}
    >
      {/* Left half - background color */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '50%',
        height: '100%',
        backgroundColor: themeData.bg
      }} />
      {/* Right half - anchor/orp color */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '50%',
        height: '100%',
        backgroundColor: themeData.orp
      }} />
    </button>
  );
}

function WordDisplay({ word, theme, size = 'large' }) {
  const fontSize = size === 'large' ? 'clamp(3rem, 8vw, 5rem)' : 'clamp(2rem, 5vw, 3rem)';
  
  if (!word) {
    return (
      <div style={{ 
        fontSize, 
        fontWeight: 300,
        color: theme.textDim,
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        ●
      </div>
    );
  }
  
  const text = word.text;
  const cleanWord = word.clean;
  const orpIndexInClean = getORPIndex(cleanWord);
  
  let cleanCharCount = 0;
  let orpIndexInOriginal = 0;
  
  for (let i = 0; i < text.length; i++) {
    if (/[a-zA-Z0-9'-]/.test(text[i])) {
      if (cleanCharCount === orpIndexInClean) {
        orpIndexInOriginal = i;
        break;
      }
      cleanCharCount++;
    }
  }
  
  const before = text.slice(0, orpIndexInOriginal);
  const orp = text[orpIndexInOriginal] || '';
  const after = text.slice(orpIndexInOriginal + 1);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: size === 'large' ? '120px' : '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize,
      fontWeight: 400,
      letterSpacing: '-0.02em',
    }}>
      <div style={{
        position: 'absolute',
        top: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '2px',
        height: '10px',
        backgroundColor: theme.orp,
        opacity: 0.5,
        borderRadius: '1px'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '2px',
        height: '10px',
        backgroundColor: theme.orp,
        opacity: 0.5,
        borderRadius: '1px'
      }} />
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        width: '100%',
        maxWidth: '90vw',
      }}>
        <span style={{ color: theme.text, textAlign: 'right', whiteSpace: 'pre' }}>
          {before}
        </span>
        <span style={{ color: theme.orp, fontWeight: 700, textAlign: 'center' }}>
          {orp}
        </span>
        <span style={{ color: theme.text, textAlign: 'left', whiteSpace: 'pre' }}>
          {after}
        </span>
      </div>
    </div>
  );
}

function MessageDisplay({ lines, theme }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      minHeight: '120px',
      textAlign: 'center',
      padding: '0 2rem'
    }}>
      {lines.map((line, i) => (
        <div 
          key={i}
          style={{
            fontSize: i === 0 ? '1.75rem' : '1.5rem',
            fontWeight: i === 0 ? 400 : 300,
            color: i === 0 ? theme.text : theme.textDim,
            fontFamily: "'Inter', system-ui, sans-serif",
            lineHeight: 1.4
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

function CountdownDisplay({ number, theme }) {
  return (
    <div style={{
      fontSize: '6rem',
      fontWeight: 200,
      color: theme.orp,
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      {number}
    </div>
  );
}

function Toggle({ enabled, onToggle, theme }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: '48px',
        height: '28px',
        borderRadius: '14px',
        backgroundColor: enabled ? theme.orp : theme.border,
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s',
        flexShrink: 0
      }}
    >
      <div style={{
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        position: 'absolute',
        top: '3px',
        left: enabled ? '23px' : '3px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
    </button>
  );
}

// ============================================
// HOME SCREEN
// ============================================
function HomeScreen({ 
  theme, 
  themeName,
  setThemeName,
  inputText, 
  setInputText, 
  targetWpm, 
  setTargetWpm,
  enableRamp,
  setEnableRamp,
  rampDuration,
  setRampDuration,
  showTutorial,
  setShowTutorial,
  onStart 
}) {
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfError, setPdfError] = useState('');
  const fileInputRef = useRef(null);
  
  const lightThemes = Object.entries(themes).filter(([_, t]) => t.isLight);
  const darkThemes = Object.entries(themes).filter(([_, t]) => !t.isLight);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      setPdfError('Please select a PDF file');
      return;
    }
    
    setPdfError('');
    setIsLoadingPDF(true);
    setPdfFileName(file.name);
    
    try {
      const text = await extractTextFromPDF(file);
      if (text.trim().length < 10) {
        setPdfError('Could not extract text from this PDF. It may be scanned or image-based.');
        setPdfFileName('');
      } else {
        setInputText(text);
      }
    } catch (err) {
      console.error('PDF extraction error:', err);
      setPdfError('Failed to read PDF. Please try another file.');
      setPdfFileName('');
    } finally {
      setIsLoadingPDF(false);
    }
  };

  const handleClearPDF = () => {
    setInputText('');
    setPdfFileName('');
    setPdfError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const wordCount = inputText.trim() ? inputText.trim().split(/\s+/).length : 0;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: `1px solid ${theme.border}`
      }}>
        <div style={{ 
          fontSize: '1.125rem', 
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ color: theme.orp, fontSize: '0.75rem' }}>●</span>
          FocusRead
        </div>
        
        {/* Theme selector with split circles */}
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {lightThemes.map(([key, t]) => (
              <ThemeButton
                key={key}
                themeKey={key}
                themeData={t}
                isSelected={themeName === key}
                onClick={() => setThemeName(key)}
              />
            ))}
          </div>
          <div style={{ width: '1px', height: '20px', backgroundColor: theme.border }} />
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {darkThemes.map(([key, t]) => (
              <ThemeButton
                key={key}
                themeKey={key}
                themeData={t}
                isSelected={themeName === key}
                onClick={() => setThemeName(key)}
              />
            ))}
          </div>
        </div>
      </header>

      <main style={{
        maxWidth: '500px',
        margin: '0 auto',
        padding: '2rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 400,
            marginBottom: '0.5rem',
          }}>
            Read faster. <span style={{ color: theme.orp, fontWeight: 500 }}>Focus deeper.</span>
          </h1>
          <p style={{ color: theme.textDim, fontSize: '0.9rem' }}>
            Configure your session, then let go.
          </p>
        </div>

        {/* Text Input Section */}
        <div>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <label style={{ 
              fontSize: '0.85rem',
              color: theme.textDim,
              fontWeight: 500
            }}>
              Your Text
            </label>
            
            {/* PDF Upload Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {pdfFileName && (
                <button
                  onClick={handleClearPDF}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'transparent',
                    color: theme.textDim,
                    border: 'none',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              )}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: theme.surface,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                {isLoadingPDF ? 'Loading...' : 'Upload PDF'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
          
          {/* Show PDF filename if loaded */}
          {pdfFileName && !pdfError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 0.875rem',
              backgroundColor: theme.surface,
              border: `1px solid ${theme.orp}40`,
              borderRadius: '0.5rem',
              marginBottom: '0.5rem',
              fontSize: '0.85rem'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.orp} strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pdfFileName}
              </span>
              <span style={{ color: theme.textDim, fontSize: '0.75rem' }}>
                {wordCount.toLocaleString()} words
              </span>
            </div>
          )}
          
          {/* Error message */}
          {pdfError && (
            <div style={{
              padding: '0.625rem 0.875rem',
              backgroundColor: theme.isLight ? '#fef2f2' : '#2d1f1f',
              border: `1px solid ${theme.isLight ? '#fecaca' : '#5c3c3c'}`,
              borderRadius: '0.5rem',
              marginBottom: '0.5rem',
              fontSize: '0.85rem',
              color: theme.isLight ? '#dc2626' : '#f87171'
            }}>
              {pdfError}
            </div>
          )}
          
          <textarea
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
              if (pdfFileName) {
                setPdfFileName('');
              }
            }}
            placeholder="Paste your article, essay, or any text here..."
            style={{
              width: '100%',
              minHeight: pdfFileName ? '80px' : '120px',
              padding: '1rem',
              backgroundColor: theme.surface,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '0.75rem',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              lineHeight: 1.5
            }}
          />
          {!inputText.trim() && (
            <p style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.8rem', 
              color: theme.textDim 
            }}>
              Leave empty to try a sample text
            </p>
          )}
          {inputText.trim() && !pdfFileName && (
            <p style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.8rem', 
              color: theme.textDim 
            }}>
              {wordCount.toLocaleString()} words
            </p>
          )}
        </div>

        {/* Reading Speed */}
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
            color: theme.textDim,
            fontWeight: 500
          }}>
            Your Reading Speed
          </label>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '0.75rem' 
          }}>
            {speedPresets.map(preset => (
              <button
                key={preset.wpm}
                onClick={() => setTargetWpm(preset.wpm)}
                style={{
                  padding: '0.875rem',
                  backgroundColor: targetWpm === preset.wpm ? theme.orp : theme.surface,
                  color: targetWpm === preset.wpm ? '#fff' : theme.text,
                  border: `1px solid ${targetWpm === preset.wpm ? theme.orp : theme.border}`,
                  borderRadius: '0.625rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.125rem' }}>
                  {preset.name}
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  opacity: targetWpm === preset.wpm ? 0.9 : 0.6 
                }}>
                  {preset.wpm} WPM · {preset.desc}
                </div>
              </button>
            ))}
          </div>
          
          <div style={{ marginTop: '1rem' }}>
            <input
              type="range"
              min="200"
              max="1000"
              step="25"
              value={targetWpm}
              onChange={(e) => setTargetWpm(Number(e.target.value))}
              style={{
                width: '100%',
                accentColor: theme.orp,
                cursor: 'pointer'
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              marginTop: '0.5rem',
              color: theme.orp,
              fontWeight: 600,
              fontSize: '1.25rem'
            }}>
              {targetWpm} WPM
            </div>
          </div>
        </div>

        {/* Settings Card */}
        <div style={{
          backgroundColor: theme.surface,
          borderRadius: '0.75rem',
          border: `1px solid ${theme.border}`,
          overflow: 'hidden'
        }}>
          {/* Guided Intro Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem',
            borderBottom: `1px solid ${theme.border}`
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Guided intro
              </div>
              <div style={{ fontSize: '0.8rem', color: theme.textDim }}>
                Learn the technique before reading
              </div>
            </div>
            <Toggle 
              enabled={showTutorial} 
              onToggle={() => setShowTutorial(!showTutorial)} 
              theme={theme} 
            />
          </div>
          
          {/* Ramp Toggle */}
          <div style={{ padding: '1rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: enableRamp ? '1rem' : 0
            }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                  Ease into your speed
                </div>
                <div style={{ fontSize: '0.8rem', color: theme.textDim }}>
                  Start slower and gradually ramp up
                </div>
              </div>
              <Toggle 
                enabled={enableRamp} 
                onToggle={() => setEnableRamp(!enableRamp)} 
                theme={theme} 
              />
            </div>
            
            {enableRamp && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {rampDurations.map(rd => (
                  <button
                    key={rd.seconds}
                    onClick={() => setRampDuration(rd.seconds)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      backgroundColor: rampDuration === rd.seconds ? theme.orp : 'transparent',
                      color: rampDuration === rd.seconds ? '#fff' : theme.text,
                      border: `1px solid ${rampDuration === rd.seconds ? theme.orp : theme.border}`,
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}
                  >
                    {rd.name}
                    <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '0.125rem' }}>
                      {rd.seconds}s
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Start Button */}
        <button
          onClick={onStart}
          disabled={isLoadingPDF}
          style={{
            padding: '1rem 2rem',
            backgroundColor: isLoadingPDF ? theme.border : theme.orp,
            color: '#fff',
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            cursor: isLoadingPDF ? 'not-allowed' : 'pointer',
            marginTop: '0.5rem',
            boxShadow: isLoadingPDF ? 'none' : `0 4px 14px ${theme.orp}40`,
          }}
        >
          Start Reading
        </button>

        <p style={{ 
          textAlign: 'center', 
          color: theme.textDim,
          fontSize: '0.75rem'
        }}>
          Space to pause · Esc to exit
        </p>
      </main>
    </div>
  );
}

// ============================================
// READING SCREEN
// ============================================
function ReadingScreen({
  theme,
  words,
  targetWpm,
  enableRamp,
  rampDuration,
  onExit
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentWpm, setCurrentWpm] = useState(enableRamp ? 300 : targetWpm);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const animationRef = useRef();
  const lastTimeRef = useRef(0);
  const accumulatorRef = useRef(0);
  const startTimeRef = useRef(0);
  const indexRef = useRef(0);
  const wpmRef = useRef(enableRamp ? 300 : targetWpm);
  
  const currentWord = words[currentIndex];
  const progress = words.length > 0 ? ((currentIndex + 1) / words.length) * 100 : 0;
  const timeRemaining = words.length > 0 
    ? Math.ceil((words.length - currentIndex - 1) / currentWpm * 60) 
    : 0;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Main reading loop
  useEffect(() => {
    if (isPaused || isComplete || words.length === 0) return;
    
    const tick = (timestamp) => {
      if (startTimeRef.current === 0) {
        startTimeRef.current = timestamp;
        lastTimeRef.current = timestamp;
      }
      
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;
      accumulatorRef.current += delta;
      
      if (enableRamp) {
        const elapsed = (timestamp - startTimeRef.current) / 1000;
        const rampProgress = Math.min(1, elapsed / rampDuration);
        const newWpm = Math.round(300 + (targetWpm - 300) * rampProgress);
        if (newWpm !== wpmRef.current) {
          wpmRef.current = newWpm;
          setCurrentWpm(newWpm);
        }
      }
      
      const msPerWord = 60000 / wpmRef.current;
      const word = words[indexRef.current];
      // 2x pause at sentence endings
      const delay = word?.isSentenceEnd ? msPerWord * SENTENCE_PAUSE_MULTIPLIER : msPerWord;
      
      if (accumulatorRef.current >= delay) {
        if (indexRef.current >= words.length - 1) {
          setIsComplete(true);
          return;
        }
        indexRef.current += 1;
        setCurrentIndex(indexRef.current);
        accumulatorRef.current = 0;
      }
      
      animationRef.current = requestAnimationFrame(tick);
    };
    
    animationRef.current = requestAnimationFrame(tick);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, isComplete, words, enableRamp, rampDuration, targetWpm]);

  // Pause on tab hidden
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        setIsPaused(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPaused(p => !p);
      }
      if (e.code === 'Escape') {
        onExit();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onExit]);

  const handleResume = () => {
    lastTimeRef.current = 0;
    accumulatorRef.current = 0;
    setIsPaused(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', system-ui, sans-serif",
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ height: '3px', backgroundColor: theme.surface }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          backgroundColor: theme.orp,
          transition: 'width 0.05s linear'
        }} />
      </div>

      <main 
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative'
        }}
        onClick={() => isPaused ? handleResume() : setIsPaused(true)}
      >
        <WordDisplay word={currentWord} theme={theme} />
        
        {isPaused && !isComplete && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `${theme.bg}ee`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2rem'
          }}>
            <div style={{ fontSize: '1.5rem', color: theme.textDim }}>
              Paused
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={(e) => { e.stopPropagation(); handleResume(); }}
                style={{
                  padding: '0.875rem 2rem',
                  backgroundColor: theme.orp,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Resume
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onExit(); }}
                style={{
                  padding: '0.875rem 2rem',
                  backgroundColor: theme.surface,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Exit
              </button>
            </div>
          </div>
        )}
        
        {isComplete && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `${theme.bg}ee`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2rem'
          }}>
            <div style={{ fontSize: '2rem', color: theme.orp, fontWeight: 300 }}>
              Complete
            </div>
            <div style={{ fontSize: '1rem', color: theme.textDim }}>
              {words.length} words · {Math.round(words.length / targetWpm * 60)}s
            </div>
            <button
              onClick={onExit}
              style={{
                padding: '0.875rem 2rem',
                backgroundColor: theme.orp,
                color: '#fff',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>
        )}
      </main>

      <footer style={{
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: theme.textDim,
        opacity: 0.6
      }}>
        <span>{currentIndex + 1} / {words.length}</span>
        <span>{currentWpm} wpm</span>
        <span>{formatTime(timeRemaining)}</span>
      </footer>
    </div>
  );
}

// ============================================
// TUTORIAL SCREEN
// ============================================
function TutorialScreen({ theme, segments, onComplete }) {
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [subState, setSubState] = useState({ type: 'init' });
  const animationRef = useRef();
  const lastTimeRef = useRef(0);
  const accumulatorRef = useRef(0);
  const wordIndexRef = useRef(0);
  
  const segment = segments[segmentIndex];
  
  useEffect(() => {
    if (!segment) {
      onComplete();
      return;
    }
    
    if (segment.type === 'message') {
      setSubState({ type: 'message', lines: segment.lines });
      const timer = setTimeout(() => {
        setSegmentIndex(i => i + 1);
      }, segment.duration);
      return () => clearTimeout(timer);
    }
    
    if (segment.type === 'countdown') {
      let count = segment.from;
      setSubState({ type: 'countdown', number: count });
      const interval = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(interval);
          setSegmentIndex(i => i + 1);
        } else {
          setSubState({ type: 'countdown', number: count });
        }
      }, 1000);
      return () => clearInterval(interval);
    }
    
    if (segment.type === 'reading') {
      const words = tokenize(segment.text);
      wordIndexRef.current = 0;
      setSubState({ type: 'reading', words, index: 0 });
      
      const msPerWord = 60000 / segment.wpm;
      
      const tick = (timestamp) => {
        if (lastTimeRef.current === 0) {
          lastTimeRef.current = timestamp;
        }
        
        const delta = timestamp - lastTimeRef.current;
        lastTimeRef.current = timestamp;
        accumulatorRef.current += delta;
        
        const word = words[wordIndexRef.current];
        // 2x pause at sentence endings
        const delay = word?.isSentenceEnd ? msPerWord * SENTENCE_PAUSE_MULTIPLIER : msPerWord;
        
        if (accumulatorRef.current >= delay) {
          wordIndexRef.current += 1;
          
          if (wordIndexRef.current >= words.length) {
            setSegmentIndex(i => i + 1);
            return;
          }
          
          setSubState({ type: 'reading', words, index: wordIndexRef.current });
          accumulatorRef.current = 0;
        }
        
        animationRef.current = requestAnimationFrame(tick);
      };
      
      lastTimeRef.current = 0;
      accumulatorRef.current = 0;
      animationRef.current = requestAnimationFrame(tick);
      
      return () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
      };
    }
  }, [segmentIndex, segment, onComplete]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', system-ui, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {subState.type === 'message' && (
        <MessageDisplay lines={subState.lines} theme={theme} />
      )}
      
      {subState.type === 'countdown' && (
        <CountdownDisplay number={subState.number} theme={theme} />
      )}
      
      {subState.type === 'reading' && subState.words[subState.index] && (
        <WordDisplay 
          word={subState.words[subState.index]} 
          theme={theme} 
          size="large"
        />
      )}
      
      <button
        onClick={onComplete}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          padding: '0.5rem 1rem',
          backgroundColor: 'transparent',
          color: theme.textDim,
          border: `1px solid ${theme.border}`,
          borderRadius: '0.375rem',
          fontSize: '0.8rem',
          cursor: 'pointer',
          opacity: 0.6
        }}
      >
        Skip intro →
      </button>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
export default function FocusRead() {
  const [screen, setScreen] = useState('home');
  
  const [themeName, setThemeName] = useState('sepia');
  const [inputText, setInputText] = useState('');
  const [targetWpm, setTargetWpm] = useState(450);
  const [enableRamp, setEnableRamp] = useState(true);
  const [rampDuration, setRampDuration] = useState(10);
  const [showTutorial, setShowTutorial] = useState(true);
  
  const [words, setWords] = useState([]);
  
  const theme = themes[themeName];

  const handleStart = () => {
    const text = inputText.trim() || sampleText;
    const tokenized = tokenize(text);
    setWords(tokenized);
    
    if (showTutorial) {
      setScreen('tutorial');
    } else {
      setScreen('reading');
    }
  };

  const handleTutorialComplete = () => {
    setScreen('reading');
  };

  const handleExit = () => {
    setScreen('home');
  };

  if (screen === 'home') {
    return (
      <>
        <HomeScreen
          theme={theme}
          themeName={themeName}
          setThemeName={setThemeName}
          inputText={inputText}
          setInputText={setInputText}
          targetWpm={targetWpm}
          setTargetWpm={setTargetWpm}
          enableRamp={enableRamp}
          setEnableRamp={setEnableRamp}
          rampDuration={rampDuration}
          setRampDuration={setRampDuration}
          showTutorial={showTutorial}
          setShowTutorial={setShowTutorial}
          onStart={handleStart}
        />
        <Analytics />
      </>
    );
  }

  if (screen === 'tutorial') {
    const segments = createTutorialSegments(targetWpm, enableRamp);
    return (
      <>
        <TutorialScreen
          theme={theme}
          segments={segments}
          onComplete={handleTutorialComplete}
        />
        <Analytics />
      </>
    );
  }

  return (
    <>
      <ReadingScreen
        key={Date.now()}
        theme={theme}
        words={words}
        targetWpm={targetWpm}
        enableRamp={enableRamp}
        rampDuration={rampDuration}
        onExit={handleExit}
      />
      <Analytics />
    </>
  );
}
